import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Star,
  Calculator,
  TrendingUp,
  Award
} from 'lucide-react';

const CriteresTab = ({ onDataChange }) => {
  // États de base
  const [criteres, setCriteres] = useState([]);
  const [typesSoutenance, setTypesSoutenance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  
  // États de recherche et pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(null);

  // États pour les filtres
  const [filters, setFilters] = useState({
    type_soutenance: ''
  });

  // États pour les modals
  const [showModal, setShowModal] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  
  // États pour les données sélectionnées
  const [selectedCritere, setSelectedCritere] = useState(null);
  const [critereToDelete, setCritereToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  // États pour le formulaire
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    type_soutenance: '',
    coefficient: '1.00',
    note_max: '20',
    ordre: '1'
  });
  const [formErrors, setFormErrors] = useState({});

  // Charger les données initiales
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: currentPage,
        page_size: pageSize,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.type_soutenance && { type_soutenance: filters.type_soutenance })
      });

      const [criteresRes, typesRes] = await Promise.all([
        api.get('/soutenance/criteres/', { params }),
        api.get('/soutenance/types-soutenance/')
      ]);

      if (criteresRes.data.status === 'success') {
        setCriteres(criteresRes.data.data || []);
        setPagination(criteresRes.data.pagination);
      }
      
      if (typesRes.data.status === 'success') {
        setTypesSoutenance(typesRes.data.data || []);
      }

    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors du chargement des données';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Gestion des changements de page
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Gestion des changements de formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal pour créer/modifier
  const handleOpenModal = (critere = null) => {
    if (critere) {
      setSelectedCritere(critere);
      setFormData({
        nom: critere.nom || '',
        description: critere.description || '',
        type_soutenance: critere.type_soutenance?.id || '',
        coefficient: critere.coefficient?.toString() || '1.00',
        note_max: critere.note_max?.toString() || '20',
        ordre: critere.ordre?.toString() || '1'
      });
    } else {
      setSelectedCritere(null);
      setFormData({
        nom: '',
        description: '',
        type_soutenance: '',
        coefficient: '1.00',
        note_max: '20',
        ordre: '1'
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation JS pour la description obligatoire
    if (!formData.description || !formData.description.trim()) {
      setFormErrors(prev => ({ ...prev, description: 'La description est obligatoire' }));
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');

      const url = selectedCritere 
        ? `/soutenance/criteres/${selectedCritere.id}/`
        : `/soutenance/types-soutenance/${formData.type_soutenance}/criteres/`;
      
      const method = selectedCritere ? 'put' : 'post';
      
      const response = await api[method](url, formData);

      if (response.data.status === 'success') {
        toast.success(selectedCritere ? 'Critère modifié avec succès' : 'Critère créé avec succès');
        await loadData();
        setShowModal(false);
        onDataChange && onDataChange();
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else {
        const errorMessage = error.response?.data?.message || 'Erreur lors de la sauvegarde';
        toast.error(errorMessage);
        setError(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Préparer la suppression
  const handlePrepareDelete = (critere) => {
    setCritereToDelete(critere);
    setConfirmConfig({
      title: 'Supprimer le critère',
      message: `Êtes-vous sûr de vouloir supprimer le critère "${critere.nom}" ?`,
      confirmText: 'Supprimer',
      type: 'danger'
    });
    setIsConfirmModalOpen(true);
  };

  // Confirmer la suppression
  const handleDelete = async () => {
    if (!critereToDelete) return;
    
    try {
      setDeleteLoading(true);
      const response = await api.delete(`/soutenance/criteres/${critereToDelete.id}/`);
      
      if (response.data.status === 'success') {
        toast.success('Critère supprimé avec succès');
        await loadData();
        onDataChange && onDataChange();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la suppression';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
      setCritereToDelete(null);
    }
  };

  // Rendu du header
  const renderHeader = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
      {/* Header principal */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Star className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Critères d'Évaluation</h2>
              <p className="text-indigo-100 text-sm">Gestion des critères d'évaluation par type de soutenance</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleOpenModal()}
              className="bg-white hover:bg-gray-50 text-indigo-600 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors font-medium"
            >
              <Plus className="h-4 w-4" />
              <span>Nouveau Critère</span>
            </button>
          </div>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Barre de recherche */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un critère..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filtres rapides */}
          <div className="flex items-center space-x-3">
            <select
              value={filters.type_soutenance || ''}
              onChange={(e) => setFilters({ ...filters, type_soutenance: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">Tous les types</option>
              {typesSoutenance.map(type => (
                <option key={type.id} value={type.id}>{type.nom}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  // Rendu de la liste des critères
  const renderCriteresList = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {loading ? (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Chargement des critères...</p>
        </div>
      ) : criteres.length === 0 ? (
        <div className="p-8 text-center">
          <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun critère trouvé</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || filters.type_soutenance 
              ? 'Aucun critère ne correspond à vos critères de recherche'
              : 'Commencez par créer votre premier critère d\'évaluation'
            }
          </p>
          {!searchTerm && !filters.type_soutenance && (
            <button
              onClick={() => handleOpenModal()}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Créer un critère
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Critère
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type de Soutenance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Coefficient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Note Max
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ordre
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {criteres.map((critere) => (
                <tr key={critere.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{critere.nom}</div>
                      <div className="text-sm text-gray-500">{critere.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {critere.type_soutenance?.nom}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {critere.coefficient}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {critere.note_max}/20
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {critere.ordre}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleOpenModal(critere)}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handlePrepareDelete(critere)}
                        className="text-red-600 hover:text-red-900 p-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Rendu du modal
  const renderModal = () => (
    <Modal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      title={selectedCritere ? 'Modifier le Critère' : 'Nouveau Critère'}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom du critère *
            </label>
            <input
              type="text"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                formErrors.nom ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ex: Qualité du contenu"
            />
            {formErrors.nom && (
              <p className="mt-1 text-sm text-red-600">{formErrors.nom}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de soutenance *
            </label>
            <select
              name="type_soutenance"
              value={formData.type_soutenance}
              onChange={handleInputChange}
              className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                formErrors.type_soutenance ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner un type</option>
              {typesSoutenance.map(type => (
                <option key={type.id} value={type.id}>{type.nom}</option>
              ))}
            </select>
            {formErrors.type_soutenance && (
              <p className="mt-1 text-sm text-red-600">{formErrors.type_soutenance}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
              formErrors.description ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Description détaillée du critère..."
            required
          />
          {formErrors.description && (
            <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Coefficient *
            </label>
            <input
              type="number"
              name="coefficient"
              value={formData.coefficient}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              max="10"
              className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                formErrors.coefficient ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {formErrors.coefficient && (
              <p className="mt-1 text-sm text-red-600">{formErrors.coefficient}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note maximale *
            </label>
            <input
              type="number"
              name="note_max"
              value={formData.note_max}
              onChange={handleInputChange}
              min="1"
              max="20"
              className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                formErrors.note_max ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {formErrors.note_max && (
              <p className="mt-1 text-sm text-red-600">{formErrors.note_max}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ordre d'affichage *
            </label>
            <input
              type="number"
              name="ordre"
              value={formData.ordre}
              onChange={handleInputChange}
              min="1"
              className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                formErrors.ordre ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {formErrors.ordre && (
              <p className="mt-1 text-sm text-red-600">{formErrors.ordre}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => setShowModal(false)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Enregistrement...' : (selectedCritere ? 'Modifier' : 'Créer')}
          </button>
        </div>
      </form>
    </Modal>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        {renderHeader()}

        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Liste des critères */}
        {renderCriteresList()}

        {/* Pagination */}
        {pagination && criteres.length > 0 && (
          <div className="mt-6">
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}

        {/* Modal */}
        {renderModal()}

        {/* Modal de confirmation */}
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleDelete}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          cancelText="Annuler"
          type={confirmConfig.type}
          isLoading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default CriteresTab; 